import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Button, Icon } from 'libs.nucleus';
import { useBooleanState } from 'libs.react';
import { ServerTimeModal } from 'libs.react_internal';
import { timeTravel as fakeTime, stopTimeTravel, getRealDate } from './fake_date.helper';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
const ONE_SECOND_MS = 1000;
const ONE_MINUTE_MS = 60 * ONE_SECOND_MS;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
export const TimeTravel = ({ className }) => {
    const [serverTime, setServerTime] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, openModal, closeModal] = useBooleanState(false);
    const { environment, studyId } = useCurrentEnvironment();
    const getServerTime = async () => {
        if (!environment?.cortexURL) {
            console.error('No environment or cortexURL available');
            setIsLoading(false);
            return;
        }
        const settimeurl = `${environment.cortexURL}/settime/`;
        try {
            const response = await fetch(settimeurl);
            const data = await response.json();
            setServerTime(data.date);
            fakeTime(data.date);
            setIsLoading(false);
        }
        catch (error) {
            console.error('Error fetching server time:', error);
            setIsLoading(false);
        }
    };
    useEffect(() => {
        // Automatically sync time on component mount
        if (environment) {
            getServerTime();
            // Set up interval to refresh every 2.5 seconds
            const interval = setInterval(() => {
                getServerTime();
            }, 2500);
            return () => {
                clearInterval(interval);
                stopTimeTravel();
            };
        }
    }, [environment]);
    const formatTime = (date) => {
        return new Date(date).toLocaleString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
        });
    };
    const calculateStudyDay = (simulationTime) => {
        const now = getRealDate();
        const simTime = new Date(simulationTime);
        const totalDiffMs = simTime.getTime() - now.getTime();
        const totalDays = totalDiffMs / ONE_DAY_MS;
        const studyDay = Math.floor(totalDays) + 1;
        const remainingMs = totalDiffMs % ONE_DAY_MS;
        const hours = Math.floor(remainingMs / ONE_HOUR_MS);
        const minutes = Math.floor((remainingMs % ONE_HOUR_MS) / ONE_MINUTE_MS);
        return `Day ${studyDay} +${hours}h ${minutes}m`;
    };
    const handleRefresh = () => {
        getServerTime();
    };
    const handleModalClose = () => {
        closeModal();
        getServerTime();
    };
    if (isLoading) {
        // Loading state
        return (_jsx("div", { className: className || '', children: _jsx(Button, { label: 'Syncing simulation time...', onClick: () => { }, variant: 'secondary', content: _jsxs("div", { className: 'flex items-center gap-2', children: [_jsx(Icon, { name: 'time', size: 20 }), _jsx("span", { className: 'font-medium', children: "Syncing simulation time..." })] }), disabled: true, className: 'shadow-sm', dataTestId: 'time-travel-loading' }) }));
    }
    if (serverTime) {
        // Active state - green button with time
        const studyDay = calculateStudyDay(serverTime);
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: className || '', children: _jsx(Button, { label: `${formatTime(serverTime)} | ${studyDay}`, onClick: openModal, variant: 'primary', content: _jsxs("div", { className: 'flex items-center gap-2 text-white', children: [_jsx(Icon, { name: 'time', size: 20, color: 'white' }), _jsxs("div", { className: 'flex flex-col items-start', children: [_jsx("span", { className: 'font-semibold text-sm', children: formatTime(serverTime) }), _jsx("span", { className: 'font-normal text-xs', children: studyDay })] })] }), disabled: !environment, className: 'bg-green-600 hover:bg-green-700 border-green-600 text-white shadow-md', dataTestId: 'time-travel-active' }) }), isModalOpen && environment && (_jsx(ServerTimeModal, { onClose: handleModalClose, serverDateTime: serverTime, studyEnv: environment, studyId: studyId }))] }));
    }
    // Error state - failed to sync
    return (_jsx("div", { className: className || '', children: _jsx(Button, { label: 'Sync simulation time', onClick: handleRefresh, variant: 'secondary', content: _jsxs("div", { className: 'flex items-center gap-2', children: [_jsx(Icon, { name: 'time', size: 20 }), _jsx("span", { className: 'font-medium', children: "Sync simulation time" })] }), disabled: !environment, className: 'shadow-sm', dataTestId: 'time-travel-inactive' }) }));
};
